import { mergeOptions } from '../../helpers/common/merge-options';
import { newGuid } from '../../helpers/common/new-guid';
import { createNode } from '../../helpers/dom/create-node';
import { getParsedCssUnit } from '../../helpers/number/get-parsed-css-unit';
import {
	ConnectedPositionStrategy, Overlay, OverlayOptions,
	OverlayPositionBuilder
} from '../../helpers/overlay';
import { RepositionScrollStrategy } from '../../helpers/overlay/scroll/reposition-scroll-strategy';

export interface SelectOptions {
	componentClassName: string;
	componentValueClassName: string;
	componentArrowClassName: string;
	panelClassName: string;
	optionClassName: string;
	activeClassName: string;
	selectedClassName: string;
	disabledClassName: string;
	iconHtml?: string;
}

export class Select {
	private overlayRef: Overlay | undefined;
	private position: ConnectedPositionStrategy | undefined;
	private select: HTMLElement;
	private selectedIndex: number;
	private activeIndex: number;
	private optionElements: HTMLElement[] = [];
	private triggerTextElement: HTMLSpanElement;
	private options: SelectOptions;
	private scrollTop = 0;
	private offsetY = 0;
	private panelOpen = false;

	static options: SelectOptions = {
		componentClassName: 'c-select',
		componentValueClassName: 'c-select__value',
		componentArrowClassName: 'c-select__arrow',
		panelClassName: 'c-select__panel',
		optionClassName: 'c-select__option',
		activeClassName: 'is-active',
		selectedClassName: 'is-selected',
		disabledClassName: 'is-disabled',
	};

	constructor(
		private element: HTMLSelectElement,
		options?: Partial<SelectOptions>
	) {
		this.options = mergeOptions(Select, options, element);

		this.selectedIndex = this.element.selectedIndex || 0;

		const selected =
			this.element.querySelector<HTMLOptionElement>('option[selected]');

		if (selected) {
			this.selectedIndex = Array.from(
				selected.parentElement?.children || []
			).indexOf(selected);
		}

		this.activeIndex = this.selectedIndex;

		this.element.tabIndex = -1;
		this.element.setAttribute('aria-hidden', 'true');

		this.select = document.createElement('div');
		this.select.id = newGuid();
		this.element.setAttribute('data-select', this.select.id);
		this.select.classList.add(this.options.componentClassName);
		this.select.tabIndex = 0;
		this.element.classList.forEach(
			(className) =>
				// we should never copy js classes, because afterwise we have an infinite loop on js-select if we add observe: true
				className.indexOf('js-') === -1 && this.select.classList.add(className)
		);

		this.triggerTextElement = document.createElement('span');
		this.triggerTextElement.classList.add(this.options.componentValueClassName);
		this.triggerTextElement.textContent =
			this.element.children[this.selectedIndex].textContent;
		this.select.appendChild(this.triggerTextElement);
		this.select.appendChild(this.triggerTextElement);

		const triggerArrow = this.options.iconHtml
			? createNode(this.options.iconHtml)
			: document.createElement('span');
		triggerArrow.classList.add(this.options.componentArrowClassName);
		this.select.appendChild(triggerArrow);

		this.element.insertAdjacentElement('afterend', this.select);
		this.element.addEventListener('focus', () => {
			this.select.focus();
			this.open();
		});

		this.select.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.toggle();
		});

		this.select.addEventListener('keydown', (event: KeyboardEvent) =>
			this._handleKeydown(event)
		);

		this.element.addEventListener('change', () => {
			this.activeIndex = this.element.selectedIndex;
			this.setSelectedValue(false);
		});
	}

	open(): void {
		if (!this.element.children.length || this.panelOpen) {
			return;
		}

		this.panelOpen = true;

		if (!this.overlayRef) {
			const panel = document.createElement('div');
			panel.classList.add(this.options.panelClassName);

			this.optionElements = Array.from(
				this.element.children as HTMLCollectionOf<HTMLOptionElement>
			).map((option) => {
				const optionElement = document.createElement('div');
				optionElement.classList.add(this.options.optionClassName);
				if (option.classList.length > 0) {
					optionElement.classList.add(...option.classList.value.split(' '));
				}
				if (option.disabled) {
					optionElement.classList.add(this.options.disabledClassName);
				}
				optionElement.classList.add(option.value);
				optionElement.textContent = option.text;
				return optionElement;
			});

			this.optionElements.forEach((optionElement) => {
				panel.appendChild(optionElement);
			});

			this.position = OverlayPositionBuilder.connectedTo(this.select)
				.withOffsetY(this.offsetY)
				.withFallbackPosition(
					{
						originX: 'start',
						originY: 'top',
					},
					{
						overlayX: 'start',
						overlayY: 'top',
					}
				)
				.withFallbackPosition(
					{
						originX: 'start',
						originY: 'bottom',
					},
					{
						overlayX: 'start',
						overlayY: 'bottom',
					}
				);

			this.overlayRef = new Overlay(
				panel,
				new OverlayOptions({
					scrollStrategy: new RepositionScrollStrategy(),
					positionStrategy: this.position,
					backdropClassName: 'c-overlay-transparent-backdrop',
					hasBackdrop: true,
				})
			).create();

			this.overlayRef.backdropClick.subscribe(() => this.close());

			this.overlayRef.afterOpened.subscribe(() => {
				if (this.panelOpen) {
					this.scrollTop = 0;
				} else {
					this.position?.withOffsetX(0);
				}
			});

			this.overlayRef.afterClosed.subscribe(() => {
				this.overlayRef?.dispose();
				this.select.focus();
			});

			this.overlayRef.onAttached.subscribe(() => {
				if (!this.overlayRef || !this.position) {
					return;
				}

				this.overlayRef.portalElement.style.transformOrigin = 'top'; // set initial origin
				const overlayRect =
					this.overlayRef.overlayElement?.getBoundingClientRect();
				const paddingWidth = Math.round(
					getParsedCssUnit(this.element.children[0], 'padding-left') / 2
				);
				const leftOverflow = 0 - (overlayRect?.left || 0);
				const rightOverflow = (overlayRect?.right || 0) - window.innerWidth;

				let offsetX = 0;
				if (leftOverflow > 0) {
					offsetX += leftOverflow + paddingWidth;
				} else if (rightOverflow > 0) {
					offsetX -= rightOverflow + paddingWidth;
				}

				this.position.withOffsetX(offsetX);
				this.overlayRef.updatePosition();
				this.overlayRef.portalElement.scrollTop = this.scrollTop;
			});

			this.overlayRef.onDetached.subscribe(() => (this.panelOpen = false));

			this.optionElements.forEach((option, index) => {
				option.addEventListener('click', () => {
					this.activeIndex = index;
					const option = this.element.children[index] as HTMLOptionElement;

					if (option.disabled) {
						return;
					}

					this.setSelectedValue();
					this.close();
				});
			});
		}

		this.overlayRef.open();
		this._calculateOverlayPosition();
		this.setActiveValue();
	}

	close(): void {
		if (!this.panelOpen) {
			return;
		}

		this.overlayRef?.close();
		this.element.dispatchEvent(new Event('blur'));
	}

	toggle(): void {
		this.panelOpen ? this.close() : this.open();
	}

	private _handleKeydown(event: KeyboardEvent) {
		event.preventDefault();
		event.stopPropagation();

		const delta = event.code === 'ArrowUp' ? -1 : 1;

		switch (event.code) {
			case 'Space':
				this.toggle();
				break;
			case 'ArrowUp':
			case 'ArrowDown':
				if (
					this.activeIndex + delta >= 0 &&
					this.activeIndex + delta < this.element.children.length
				) {
					for (let i = 1; i <= this.element.children.length; i++) {
						const index =
							(this.activeIndex + delta * i + this.element.children.length) %
							this.element.children.length;

						if (!(this.element.children[index] as HTMLOptionElement).disabled) {
							this.activeIndex = index;
							break;
						}
					}
				}

				if (this.panelOpen) {
					this.setActiveValue();
				} else {
					this.setSelectedValue();
				}
				break;
			case 'Escape':
				this.close();
				break;
			case 'Enter':
				if (!this.panelOpen) {
					this.open();
				} else {
					this.setSelectedValue();
					this.close();
				}
				break;
		}
	}

	private setSelectedValue(shouldForwardEvent = true) {
		const option = this.element.children[this.activeIndex] as HTMLOptionElement;

		if (this.select.getAttribute('data-value') !== option.value) {
			this.triggerTextElement.textContent = option.text;
			this.select.setAttribute('data-value', option.value);
			this.element.value = option.value;
			this.selectedIndex = this.activeIndex;
			this.element.selectedIndex = this.selectedIndex;

			this.setActiveValue();

			if (shouldForwardEvent) {
				this.element.dispatchEvent(new Event('change'));
			}
		}
	}

	private setActiveValue(): void {
		if (this.overlayRef) {
			const itemHeight = this.optionElements[0]?.offsetHeight || 0;
			const scrollOffset = this.activeIndex * itemHeight;
			const maxHeight = getParsedCssUnit(
				this.overlayRef.portalElement,
				'max-height'
			);

			if (scrollOffset < this.overlayRef.portalElement.scrollTop) {
				this.overlayRef.portalElement.scrollTop = scrollOffset;
			} else if (
				scrollOffset + itemHeight >
				this.overlayRef.portalElement.scrollTop + maxHeight
			) {
				this.overlayRef.portalElement.scrollTop = Math.max(
					0,
					scrollOffset - maxHeight + itemHeight
				);
			}
		}

		this.optionElements?.forEach((option, index) => {
			option.classList.toggle(
				this.options.selectedClassName,
				index === this.selectedIndex
			);
			option.classList.toggle(
				this.options.activeClassName,
				index === this.activeIndex
			);
		});
	}

	private _calculateOverlayPosition() {
		if (!this.overlayRef) {
			return;
		}

		const itemHeight = this.optionElements[0]?.offsetHeight || 0;
		const maxHeight = getParsedCssUnit(
			this.overlayRef.portalElement,
			'max-height'
		);
		const items = this.element.children.length;
		const panelHeight = Math.min(items * itemHeight, maxHeight);
		const scrollContainerHeight = items * itemHeight;
		const maxScroll = scrollContainerHeight - panelHeight;
		const scrollBuffer = panelHeight / 2;
		const optionOffsetFromScrollTop = itemHeight * this.selectedIndex;
		const halfOptionHeight = itemHeight / 2;
		const optimalScrollPosition =
			optionOffsetFromScrollTop - scrollBuffer + halfOptionHeight;
		this.scrollTop = Math.min(Math.max(0, optimalScrollPosition), maxScroll);
		const triggerRect = this.select.getBoundingClientRect();
		const optionHeightAdjustment = (itemHeight - triggerRect.height) / 2;
		const maxOptionsDisplayed = Math.floor(maxHeight / itemHeight);
		let optionOffsetFromPanelTop: number;

		if (this.scrollTop === 0) {
			optionOffsetFromPanelTop = this.selectedIndex * itemHeight;
		} else if (this.scrollTop === maxScroll) {
			const firstDisplayedIndex = items - maxOptionsDisplayed;
			const selectedDisplayIndex = this.selectedIndex - firstDisplayedIndex;
			const partialItemHeight =
				itemHeight - ((items * itemHeight - maxHeight) % itemHeight);

			optionOffsetFromPanelTop =
				selectedDisplayIndex * itemHeight + partialItemHeight;
		} else {
			optionOffsetFromPanelTop = scrollBuffer - itemHeight / 2;
		}

		this.offsetY = optionOffsetFromPanelTop * -1 - optionHeightAdjustment;

		const viewportSize = {
			width: window.innerWidth,
			height: window.innerHeight,
		};
		const paddingWidth = Math.round(
			getParsedCssUnit(this.optionElements[0], 'padding-left') / 2
		);

		const topSpaceAvailable = triggerRect.top - paddingWidth;
		const bottomSpaceAvailable =
			viewportSize.height - triggerRect.bottom - paddingWidth;

		const panelHeightTop = Math.abs(this.offsetY);
		const totalPanelHeight = Math.min(
			this.element.children.length * itemHeight,
			maxHeight
		);
		const panelHeightBottom =
			totalPanelHeight - panelHeightTop - triggerRect.height;

		const originY =
			Math.abs(this.offsetY) - optionHeightAdjustment + itemHeight / 2;
		const originBasedOnOption = `50% ${originY}px 0px`;

		if (panelHeightBottom > bottomSpaceAvailable) {
			const distanceBelowViewport = Math.round(
				panelHeightBottom - bottomSpaceAvailable
			);

			this.scrollTop -= distanceBelowViewport;
			this.offsetY -= distanceBelowViewport;
			this.overlayRef.portalElement.style.transformOrigin = originBasedOnOption;

			if (triggerRect.top + this.scrollTop <= 0) {
				this.scrollTop = 0;
				this.offsetY = 0;
				this.overlayRef.portalElement.style.transformOrigin = '50% bottom 0px';
			}
		} else if (panelHeightTop > topSpaceAvailable) {
			const distanceAboveViewport = Math.round(
				panelHeightTop - topSpaceAvailable
			);

			this.scrollTop += distanceAboveViewport;
			this.offsetY += distanceAboveViewport;
			this.overlayRef.portalElement.style.transformOrigin = originBasedOnOption;

			if (triggerRect.top + this.scrollTop >= maxScroll) {
				this.scrollTop = maxScroll;
				this.offsetY = 0;
				this.overlayRef.portalElement.style.transformOrigin = '50% top 0px';
				return;
			}
		} else {
			this.overlayRef.portalElement.style.transformOrigin = originBasedOnOption;
		}

		this.position?.withOffsetY(this.offsetY);
	}
}
